import { Injectable } from '@angular/core';
import { DateFormatModel } from '@core/date.model';
import * as moment from 'moment';
import 'moment-duration-format';
import { DataFormatterService, DATE_FORMATS, TIME_FORMATS } from 'tis-data-formatter';

@Injectable({
	providedIn: 'root'
})
export class DateFormatService {

	constructor(private dataFormatterService: DataFormatterService) {}

	formatToLocalTime(date: string | moment.Moment, format?: string): string {
		return moment.utc(date).local().format(format);
	}

	getToday(): string {
		return moment().format();
	}

	getMonth(date: string = this.getToday(), format: string = 'MMMM'): string {
		return moment(date).format(format);
	}

	getStartOf(date: string | number | Date | undefined, unitOfTime: moment.unitOfTime.StartOf, format?: string): string {
		return this.dataFormatterService.date(date, {ignoreTimezone: true})
			.startOf(unitOfTime)
			.format(format);
	}

	getEndOf(date: string | number | Date | undefined, unitOfTime: moment.unitOfTime.StartOf, format?: string): string {
		return this.dataFormatterService.date(date, {ignoreTimezone: true})
			.endOf(unitOfTime)
			.format(format);
	}

	getDiffInDays(dateFrom: string, dateTo: string): number {
		const dateFromObj: moment.Moment = moment(dateFrom).startOf('day');
		const dateToObj: moment.Moment = moment(dateTo).startOf('day');
		return Math.ceil(moment.duration(dateFromObj.diff(dateToObj)).asDays());
	}

	getDateFromDaysBefore(date: string, daysNumber: number, format?: string): string {
		return moment(date)
			.subtract(daysNumber, 'day')
			.format(format);
	}

	formatDuration(data: moment.DurationInputArg1, args: string[]): string {
		const [sourceFormat, destinationFormat, abbreviation]: string[] = args;
		let formattedTime: string = data as string;
		if (destinationFormat) {
			formattedTime = moment
				.duration(data, sourceFormat as moment.DurationInputArg2)
				.format(destinationFormat, {trim: false});
		}
		if (abbreviation) {
			const [splittedThousands, splittedHours]: string[] = formattedTime.split(',');
			formattedTime = splittedHours ? `${splittedThousands}.${splittedHours.charAt(0)}${abbreviation}` : formattedTime;
		}
		return formattedTime;
	}

	prepareApiDatesRange(dateFrom: string, dateTo: string, utc: boolean = true): DateFormatModel {
		const method = utc ? this.formatUtcDate.bind(this) : this.dataFormatterService.formatDate.bind(this);
		const format: string = 'YYYY-MM-DD';
		return {
			from: method(dateFrom, undefined, format),
			to: method(dateTo, undefined, format)
		};
	}

	getTodayDatesRange(): DateFormatModel {
		const today: string = this.getToday();
		return this.prepareApiDatesRange(today, today);
	}

	getLastMonthDatesRange(): DateFormatModel {
		const today: string = this.getToday();
		return this.prepareApiDatesRange(
			this.getStartOf(today, 'month'),
			this.getEndOf(today, 'month'),
		);
	}

	getLastDaysRange(days: number = 90): DateFormatModel {
		const today: string = this.getToday();
		return this.prepareApiDatesRange(
			this.getDateFromDaysBefore(today, days),
			today
		);
	}

	convertMinutesToHours(numberOfMinutes: number): string | null {
		let result: string | null = null;
		if (numberOfMinutes || numberOfMinutes === 0) {
			const absoluleMinutes = Math.abs(numberOfMinutes);
			const minutes = absoluleMinutes % 60 < 10 ? '0' + absoluleMinutes % 60 : absoluleMinutes % 60;
			const convertedTime = numberOfMinutes < 60 ? '00:' + minutes : Math.floor(numberOfMinutes/60) + ':' + minutes;
			result = convertedTime;
		}
		return result;
	}

	formatUtcDate(date: string | moment.Moment, format?: DATE_FORMATS, customFormat?: string) {
		return this.dataFormatterService.formatDate(date, format, customFormat, 0, false);
	}

	formatUtcTime(date: string | moment.Moment, format?: TIME_FORMATS, customFormat?: string) {
		return this.dataFormatterService.formatTime(date, format, customFormat, 0, false);
	}

}
